/**
 * When a tenant's key-pickup details become visible (COL-533).
 *
 * Lifted VERBATIM out of `CheckInCheckOut.tsx`, where it was private to a
 * 1,000-line V1 component. The V2 room page has to apply the same rule, and
 * the one thing that must not happen is two implementations of "when do the
 * keys unlock" drifting apart — one of them would then either reveal a pickup
 * code early or hide it after it was due.
 *
 * The rule: details unlock at 00:01 Berlin time, one day before this tenant's
 * move-in. Berlin rather than the device's timezone because the properties are
 * in Germany and the shop opens on German time; a tenant travelling should not
 * see a different unlock moment from one sitting in Munich.
 */

/** KeyNest pickup details unlock this many days before move-in. */
export const KEYNEST_UNLOCK_DAYS_BEFORE = 1;

/** Wall-clock time, in the property's timezone, at which they unlock. */
export const KEYNEST_UNLOCK_HOUR = 0;
export const KEYNEST_UNLOCK_MINUTE = 1;

/** Germany. Fixed, so the unlock instant does not follow the tenant's device. */
export const PROPERTY_TIME_ZONE = "Europe/Berlin";

/**
 * A calendar date ("YYYY-MM-DD", or a full ISO timestamp) as a Date at LOCAL
 * midnight. Any time/zone suffix is ignored on purpose: the unlock is judged
 * on the calendar day, not a UTC instant, so the card does not flicker on and
 * off around midnight for tenants in other timezones.
 */
export function parseLocalDate(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw).trim());
  if (!m) return null;
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/** Offset (ms) of `timeZone` from UTC at `instant`. Positive east of UTC. */
export function tzOffsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);
  const map: Record<string, number> = {};
  for (const p of parts) {
    if (p.type !== "literal") map[p.type] = Number(p.value);
  }
  const asUTC = Date.UTC(
    map.year,
    map.month - 1,
    map.day,
    map.hour,
    map.minute,
    map.second
  );
  return asUTC - instant.getTime();
}

/**
 * A wall-clock time in `timeZone`, as the matching absolute instant.
 *
 * DST is handled via the offset at the candidate instant; 00:01 never lands on
 * a Berlin transition, which happen at 02:00/03:00.
 */
export function zonedWallTimeToInstant(
  year: number,
  monthIndex: number,
  day: number,
  hour: number,
  minute: number,
  timeZone: string
): Date {
  const naiveUTC = Date.UTC(year, monthIndex, day, hour, minute);
  const offset = tzOffsetMs(new Date(naiveUTC), timeZone);
  return new Date(naiveUTC - offset);
}

/**
 * The instant this booking's pickup details unlock, or null when the booking
 * carries no usable date.
 *
 * Null means FAIL OPEN — `keynestUnlocked` treats it as unlocked — because the
 * alternative is hiding pickup details from a tenant we simply cannot gate,
 * who then arrives at the shop with no code. That is the V1 behaviour and it
 * is deliberate.
 */
export function keynestUnlockAt(booking: any): Date | null {
  const moveIn = parseLocalDate(booking?.move_in_date || booking?.start_date);
  if (!moveIn) return null;
  const unlockDay = new Date(moveIn);
  unlockDay.setDate(unlockDay.getDate() - KEYNEST_UNLOCK_DAYS_BEFORE);
  return zonedWallTimeToInstant(
    unlockDay.getFullYear(),
    unlockDay.getMonth(),
    unlockDay.getDate(),
    KEYNEST_UNLOCK_HOUR,
    KEYNEST_UNLOCK_MINUTE,
    PROPERTY_TIME_ZONE
  );
}

/** Whether pickup details are visible to this booking at `nowMs`. */
export function keynestUnlocked(booking: any, nowMs: number): boolean {
  const at = keynestUnlockAt(booking);
  return !at || nowMs >= at.getTime();
}
